import { Observable } from 'rxjs';

import { OperationDescriptor } from './operation.model';
import { ProcessingImage } from './processing-image';

/** Progress snapshot for an in-flight model download. `null` = no active download. */
export interface ModelDownloadProgress {
  modelUrl: string;
  loaded: number;
  total: number | null;
  pct: number | null;
}

/** A rectangle of the full-resolution image, in its own pixels. */
export interface SourceRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The extra surface a **browser preview engine** exposes to the pipeline dialog:
 * what it is doing, how far a model download has got, and — for engines whose
 * output is geometry rather than pixels — the regions to draw on the preview.
 *
 * Declared as its own interface, and discovered structurally, so the dialog does
 * not name engines. It used to check for `'yolo-seg'` by id, which meant the
 * retinal engine shipped inert: no progress, no status, no preview regions, and
 * a null source rect — which silently commits regions at crop-local coordinates,
 * i.e. the wrong place on the slide. Adding a fourth engine should not require
 * editing the dialog again.
 */
export interface PreviewFeedEngine {
  progress$: Observable<ModelDownloadProgress | null>;
  status$: Observable<string | null>;
  /** Regions in the step image's own pixels. Absent for engines that output pixels. */
  previewRegions$?: Observable<unknown[]>;
  /**
   * Where in the full-resolution image the pipeline's input pixels came from.
   * Engines that commit regions to the viewer need it, or their output lands at
   * crop-local coordinates.
   */
  setSourceRect?(rect: SourceRect | null): void;
  /**
   * The `Region.source` marker this engine stamps on its output, so the preview
   * can replace one engine's regions without touching another's.
   */
  readonly regionSource?: string;
}

/**
 * Whether an engine publishes the preview feeds.
 *
 * The predicate narrows to the intersection rather than to `PreviewFeedEngine`
 * alone, so `getEngines().filter(hasPreviewFeeds)` keeps the `ProcessingEngine`
 * side of the type instead of discarding it.
 */
export function hasPreviewFeeds<T>(engine: T): engine is T & PreviewFeedEngine {
  const e = engine as Partial<PreviewFeedEngine> | null;
  return (
    !!e && typeof e.progress$?.subscribe === 'function' && typeof e.status$?.subscribe === 'function'
  );
}

/**
 * A processing engine wraps a specific library (transformers.js, OpenCV.js, server, etc.)
 * and exposes its capabilities through a uniform interface.
 */
export interface ProcessingEngine {
  /** Unique engine identifier, e.g. 'opencv', 'transformers-js', 'server'. */
  readonly id: string;

  /** Human-readable engine name. */
  readonly name: string;

  /** Operations this engine provides. Populated after initialize(). */
  readonly operations: OperationDescriptor[];

  /** Load the engine (download WASM, models, etc.). Called lazily on first use. */
  initialize(): Promise<void>;

  /** Whether initialize() has been called successfully. */
  isInitialized(): boolean;

  /** Execute an operation on an input image with given parameters. */
  execute(
    operation: OperationDescriptor,
    input: ProcessingImage,
    params: Record<string, any>
  ): Promise<ProcessingImage>;

  /** Release resources (WASM memory, model tensors, etc.). */
  dispose(): void;

  /** Abort any in-progress long-running operation (e.g. SAM auto-segment). */
  cancelCurrentOperation?(): void;
}
